/* HLS mobile "scroll-to-flip" second product image (Nir, 2026-07-21)
 *
 * Desktop reveals a product's second image on hover. Touch screens have no
 * hover, so on mobile we flip a card to its second (back-of-garment) image
 * ONLY while it sits in the vertical middle band of the viewport:
 *
 *   - On load, the top row is near the top of the screen (NOT the middle), so
 *     it shows its FIRST image.
 *   - As you scroll down, each card flips to its SECOND image when it reaches
 *     the middle of the screen, and flips BACK to its first image once it
 *     leaves the middle (whether it scrolls up past the top or hasn't reached
 *     the middle yet).
 *   - So at any moment only the row currently centered shows its second image;
 *     the rows above and below show their first. No latching.
 *
 * The CSS that swaps the images lives in hls-redesign.css (.hls-flip-active,
 * scoped to max-width:989px); this script only toggles that class.
 *
 * Design notes:
 *  - Runs only on narrow / touch viewports (<=989px), matching the CSS.
 *  - Scroll work is throttled through requestAnimationFrame (one pass/frame).
 *  - Only cards that actually HAVE a second image are tracked.
 *  - Honors prefers-reduced-motion by not activating at all.
 *  - Re-scans after Shopify section re-renders (theme editor, AJAX filters).
 */
(function () {
  'use strict';

  var MOBILE_QUERY = '(max-width: 989px)';
  var REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Half-height (in px) of the center band around the viewport middle. A card
  // is "active" when its own vertical center falls within middle ± this value.
  // ~22% of the screen height gives a comfortable strip: a card flips a little
  // before it's dead-center and flips back a little after.
  var BAND_RATIO = 0.22;

  var cards = [];
  var ticking = false;
  var active = false;
  var settleTimer = null;

  function isMobile() {
    return window.matchMedia(MOBILE_QUERY).matches;
  }

  // A card is eligible only if it has a real second image to reveal.
  function collectCards() {
    cards = [];
    var wrappers = document.querySelectorAll('.card-wrapper');
    for (var i = 0; i < wrappers.length; i++) {
      // The Last Sizes strip turns its own cards on a timer
      // (hls-last-sizes.js); two flippers on one card would fight.
      if (wrappers[i].closest('.hls-lastsizes')) continue;
      var imgs = wrappers[i].querySelectorAll('.media--hover-effect > img');
      if (imgs.length >= 2) cards.push(wrappers[i]);
    }
  }

  // Flip a card only while its center sits inside the middle band; otherwise
  // (above OR below the band) it shows its first image. No latching.
  function update() {
    ticking = false;
    var vh = window.innerHeight;
    var middle = vh / 2;
    var band = vh * BAND_RATIO;
    for (var i = 0; i < cards.length; i++) {
      var rect = cards[i].getBoundingClientRect();
      var cardCenter = rect.top + rect.height / 2;
      var inBand = Math.abs(cardCenter - middle) <= band;
      var isFlipped = cards[i].classList.contains('hls-flip-active');
      if (inBand && !isFlipped) {
        cards[i].classList.add('hls-flip-active');
      } else if (!inBand && isFlipped) {
        cards[i].classList.remove('hls-flip-active');
      }
    }
  }

  function onScroll() {
    if (!ticking) {
      ticking = true;
      window.requestAnimationFrame(update);
    }
    // Fast momentum scrolling (esp. iOS) coalesces scroll events through rAF,
    // so the frame that fires as scrolling abruptly STOPS — e.g. slamming back
    // to the top — can measure a mid-flight position and leave a card stuck on
    // its second image with no later event to correct it. A short settle timer
    // runs one more update once scrolling has quieted, snapping every card to
    // its true final state. (Nir, 2026-07-21: top row stuck flipped after
    // scrolling to the bottom and back up.)
    if (settleTimer) clearTimeout(settleTimer);
    settleTimer = setTimeout(update, 120);
  }

  function connect() {
    if (REDUCED_MOTION || !isMobile() || active) return;
    collectCards();
    if (!cards.length) return;
    active = true;
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update(); // set initial state for whatever is already on screen
  }

  function disconnect() {
    if (!active) return;
    active = false;
    if (settleTimer) { clearTimeout(settleTimer); settleTimer = null; }
    window.removeEventListener('scroll', onScroll);
    window.removeEventListener('resize', onScroll);
    for (var i = 0; i < cards.length; i++) cards[i].classList.remove('hls-flip-active');
  }

  // React to crossing the mobile/desktop boundary (rotation, resize, devtools).
  var mq = window.matchMedia(MOBILE_QUERY);
  function onBreakpointChange() {
    if (isMobile()) connect();
    else disconnect();
  }
  if (mq.addEventListener) mq.addEventListener('change', onBreakpointChange);
  else if (mq.addListener) mq.addListener(onBreakpointChange); // older Safari

  // Shopify fires this after section re-renders in the theme editor and after
  // some AJAX interactions; rebuild the card list so new cards are tracked.
  document.addEventListener('shopify:section:load', function () {
    if (active) { active = false; connect(); }
    else connect();
  });

  // Facet filtering replaces the whole grid via the Section Rendering API,
  // which leaves `cards` holding detached nodes — scroll-flip then silently
  // does nothing for every card on screen. shopify:section:load does NOT fire
  // for those swaps on the live storefront (theme editor only), so watch the
  // container itself. Matters on every collection view since hls-instock-filter
  // re-renders the grid on landing to correct the size counts.
  // (Nir, 2026-07-30.)
  function refresh() {
    if (REDUCED_MOTION || !isMobile()) return;
    if (!active) {
      connect();
      return;
    }
    collectCards();
    update();
  }

  var gridContainer = document.getElementById('ProductGridContainer');
  if (window.MutationObserver && gridContainer) {
    var refreshTimer = null;
    // childList only: update() touches classes, which cannot retrigger this.
    new MutationObserver(function () {
      if (refreshTimer) clearTimeout(refreshTimer);
      refreshTimer = setTimeout(refresh, 50);
    }).observe(gridContainer, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', connect);
  } else {
    connect();
  }
})();
